import React from "react";
import * as Party2 from "../../../../../domain/models/Party2";
import * as Listing3 from "../../../../../domain/models/Listing3";
import * as PaginationMeta1 from "../../../../../domain/models/PaginationMeta1";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { CRMTitleSection } from "../../CRMTitleSection/CRMTitleSection";
import { CRMEmptyPlaceholder } from "../../Simple/CRMEmptyPlaceholder/CRMEmptyPlaceholder";
import { CRMCardOutside } from "../../CRMCardOutside/CRMCardOutside";
import { Padding } from "../../BuildingBlocks/Padding";
import { CRMColors } from "../../../models/CRMColors";
import { CRMListingTable } from "../../CRM/CRMListings/CRMListingTable";
import { CRMPartyTable } from "../../CRM/CRMParties/CRMPartyTable";
import { CRMSearchOverlayShell } from "./CRMSearchOverlayShell";

type TCRMHomesSearchOverlayProps = {
    isOpen: boolean;
    searchValue: string;
    listings: Array<Listing3.T>;
    listingsMeta: PaginationMeta1.T;
    parties: Array<Party2.T>;
    partiesMeta: PaginationMeta1.T;
    onChange: (searchValue: string) => void;
    onClose: () => void;
    onLoadMoreListings: () => void;
    onLoadMoreParties: () => void;
    onListingClick: (listing: Listing3.T) => void;
    onSellerClick: (listing: Listing3.T) => void;
    onPartyClick: (party: Party2.T) => void;
};

const countLabel = (meta: PaginationMeta1.T): string =>
    meta.available_count > meta.returned_count
        ? `${meta.returned_count} / ${meta.available_count}`
        : meta.available_count.toString();

const hasMore = (meta: PaginationMeta1.T): boolean =>
    meta.available_count > meta.returned_count;

export const CRMHomesSearchOverlay = (props: TCRMHomesSearchOverlayProps): JSX.Element => {
    const hasSearched = props.searchValue.trim() !== "";
    const hasListings = props.listings.length > 0;
    const hasParties = props.parties.length > 0;

    // Sail Legal renders nothing at all when a search matches no cases. Two headed but
    // empty tables read as a broken page rather than an answer, so a search with no
    // matches gets one placeholder and a section only appears once it has rows.
    const hasNothing = hasSearched && ! hasListings && ! hasParties;

    return (
        <CRMSearchOverlayShell
            isOpen={props.isOpen}
            searchValue={props.searchValue}
            placeholder="Search for a buyer, seller or property..."
            onChange={props.onChange}
            onClose={props.onClose}
        >
            {hasNothing &&
                // On a card, like the results are. Floated straight onto the shadowbox the
                // text sits over whatever page is behind the overlay and is unreadable.
                <CRMCardOutside borderRounding="right" shadow={true}>
                    <Padding type="vertical" spacing={CRMSpacing.X_LARGE}>
                        <CRMEmptyPlaceholder
                            iconSize="90px"
                            iconColor={CRMColors.NEUTRAL_10}
                            fontColor={CRMColors.NEUTRAL_2}
                            textGap={CRMSpacing.MEDIUM}
                        >
                            {`Nothing matches “${props.searchValue.trim()}”`}
                        </CRMEmptyPlaceholder>
                    </Padding>
                </CRMCardOutside>
            }

            {hasSearched && hasListings &&
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <CRMTitleSection>{`Listings (${countLabel(props.listingsMeta)} results)`}</CRMTitleSection>
                    <CRMListingTable
                        listings={props.listings}
                        showLoadMore={hasMore(props.listingsMeta)}
                        onLoadMore={props.onLoadMoreListings}
                        onListingClick={props.onListingClick}
                        onSellerClick={props.onSellerClick}
                    />
                </SpacingColumn>
            }

            {hasSearched && hasParties &&
                <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                    <CRMTitleSection>{`Parties (${countLabel(props.partiesMeta)} results)`}</CRMTitleSection>
                    <CRMPartyTable
                        parties={props.parties}
                        showLoadMore={hasMore(props.partiesMeta)}
                        onLoadMore={props.onLoadMoreParties}
                        onPartyClick={props.onPartyClick}
                    />
                </SpacingColumn>
            }
        </CRMSearchOverlayShell>
    );
};
